// TODO: Colocated with custom-collections because custom-collections is the only consumer.
// Mirrors the inline `runHooks` function in step-wrapper.tsx, which covers the
// same hook handlers (`corelation_credit_pull`, `fis_gkyc_evaluation`,
// `experian_queries`) and the same `retryHandler` retry loop. Keep both in
// sync. If a future refactor shares lifecycle between step-wrapper and
// custom-collections, relocate this up to `src/utils/` and collapse the inline copy.
import { message } from "antd";
import { Hook, OnboardingApplication } from "@dreambigger/shared/src/types";
import { useApplication } from "../../../api";

type ApplicationHelper = ReturnType<typeof useApplication>;

/**
 * Run the hooks configured under `assets.beforeHooks.hooks` or
 * `assets.afterHooks.hooks` for a step. Shared between StepWrapper (v1) and
 * custom-collections so the hook-execution pipeline only exists in one place — the
 * set of supported hook names, their retry behavior, and their polling is a
 * single source of truth.
 *
 * Each hook is looked up in `hookHandlers` by `hook.name`, wrapped in
 * `retryHandler` (up to `hook.retryCount` attempts, default 1), and the
 * results are returned as a `Promise.allSettled` so callers can decide
 * whether to gate rendering / navigation on individual failures.
 */
export default async function runStepHooks({
  hooks,
  applicationHelper,
  application,
}: {
  hooks: Hook[];
  applicationHelper: ApplicationHelper;
  application: OnboardingApplication;
}) {
  const retryHandler = async ({
    hook,
    callback,
  }: {
    hook: Hook;
    callback: () => Promise<void>;
  }) => {
    const retryCount = hook.retryCount || 1;

    let attempts = 0;
    let success = false;

    while (attempts < retryCount && !success) {
      try {
        await callback();
        success = true;
      } catch (error) {
        attempts += 1;

        if (attempts >= retryCount) {
          message.error("Something went wrong. Please try again.");
          throw error;
        }
      }
    }
  };

  const handleCorelationCreditPull = async ({
    application,
    hook,
  }: {
    application: OnboardingApplication;
    hook: Hook;
  }) => {
    const { data: creditReports } =
      await applicationHelper.pullCorelationCredit(application.id);

    const promises = creditReports
      .filter((cr) => !cr.received)
      .map(async ({ id }) => {
        const DELAY_MS = hook.polling?.delayMs || 3000;
        const MAX_ATTEMPTS = hook.polling?.maxAttempts || 10;

        const delay = () =>
          new Promise((resolve) => setTimeout(resolve, DELAY_MS));

        let attempts = 0;

        while (attempts < MAX_ATTEMPTS) {
          const { data: creditReport } =
            await applicationHelper.getFinancialApplicationCreditReport(id);

          if (creditReport.received) {
            return true;
          }

          attempts++;
          await delay();
        }

        return false;
      });

    const results = await Promise.allSettled(promises);
    let hasErrors = false;
    let missingReports = false;
    results.forEach((result) => {
      if (result.status === "rejected") {
        hasErrors = true;
      } else if (result.value === false) {
        missingReports = true;
      }
    });

    if (hasErrors) {
      throw new Error("Failed to run corelation credit pull hook.");
    }

    if (missingReports) {
      throw new Error("Failed to pull data for corelation credit pull.");
    }
  };

  const handleFisGkycEvaluation = async ({
    application,
  }: {
    application: OnboardingApplication;
  }) => {
    try {
      await applicationHelper.runFisGkycEvaluations(application.id);
    } catch (error) {
      throw new Error("Failed to run FIS GKYC evaluation hook.");
    }
  };

  const handleExperianQueries = async ({
    application,
  }: {
    application: OnboardingApplication;
  }) => {
    try {
      await applicationHelper.runExperianQueries(application.id);
    } catch (error) {
      throw new Error("Failed to run Experian Queries hook.");
    }
  };

  const hookHandlers: Record<string, (hook: Hook) => Promise<void>> = {
    corelation_credit_pull: async (hook: Hook) => {
      await retryHandler({
        hook,
        callback: () => handleCorelationCreditPull({ application, hook }),
      });
    },
    fis_gkyc_evaluation: async (hook: Hook) => {
      await retryHandler({
        hook,
        callback: () => handleFisGkycEvaluation({ application }),
      });
    },
    experian_queries: async (hook: Hook) => {
      await retryHandler({
        hook,
        callback: () => handleExperianQueries({ application }),
      });
    },
  };

  const validHooks = hooks.filter((hook) => hook.slug);

  const hookPromises = validHooks.map(async (hook) => {
    try {
      const handler = hookHandlers[hook.name];

      if (handler) {
        await handler(hook);
      } else {
        console.warn(`Unrecognized hook: ${hook.name}`);
      }

      return { hookSlug: hook.slug, status: "fulfilled" };
    } catch (error) {
      throw {
        hookSlug: hook.slug,
        status: "rejected",
        reason: error instanceof Error ? error.message : error,
      };
    }
  });

  return Promise.allSettled(hookPromises);
}
